/**
 * The Overview's band. A dashboard: title only, no back arrow.
 *
 * THE STAT STRIP IS EVERY BAND VERTICAL'S (api#1165), on a positive guard: a
 * company with casing, trucking, rentals, downhole, pressure testing, NDT or
 * pipeline gets it (NDT since api#1238); one with none of them gets the band
 * with no strip. Open work, to approve and equipment out are GET /dashboard/band's
 * totals; certs is the team cert summary.
 *
 * A CASING-ONLY COMPANY'S BAND IS BYTE-IDENTICAL to the one that tallied
 * /dispatch/jobs and /equipment in the browser: the same labels, the same
 * sub-lines, the same figures (OverviewBand.casing-golden.test.tsx, written by
 * the pre-switch band). Every other company reads the same columns under
 * vertical-neutral words of the same length, because the third column's label
 * ceiling (~70px) and the sub-lines were measured against the hero circle.
 *
 * A figure no vertical could compute, or one the role is refused, is an em
 * dash. A PARTIAL total (some vertical computed, another did not) carries
 * "Partial count" in place of its sub-line: a floor read as the whole count
 * is the wrong number on a dashboard.
 *
 * Every figure is gated on its own read arriving. Still loading is a pulse; a
 * failed or refused read is an em dash (fe#758), not a pulse that never ends
 * and never a zero.
 *
 * The Scorecard widget that repeated these figures is retired (2026-10-03).
 * Its breakdowns are NOT on the band. A sub-line that can show only part of a
 * breakdown shows none of it: "1 active" under 3 open jobs reads as the whole
 * split, and it is not. The full job split ran 119-255px at a 1024 viewport as
 * a tenant's open statuses grew (columns there are 150/130/130/150px), the
 * equipment split 124px against column 3's ~80px sub-line ceiling, and
 * "N current" would take the expiring line to 120px in column 4. The full
 * breakdowns live on the Equipment Tracker and Safety & Certs widgets.
 * "N expiring" under Certs expired is not a partial split: it is the one
 * other bucket that matters on the day (fe#757), not a share of the figure.
 */
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Briefcase, ClipboardCheck, Plus, Wrench } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { PageHeaderBand, type PageHeaderStat } from '../layout/PageHeaderBand';
import { useBandFigures, type CountFigure, type Figure } from './bandFigures';
import { hasGlobalMoneyVertical } from './globalMoney';

const ABSENT = '—';

/** The verticals GET /dashboard/band counts (fieldiq-api BAND_ADAPTERS). */
export const BAND_VERTICALS = ['casing_running', 'trucking', 'surface_rentals', 'downhole_tools', 'pressure_testing', 'ndt_inspection', 'pipeline_facility', 'well_testing', 'well_servicing'] as const;

/**
 * In place of the sub-line when the total is a floor. Measured in the band's
 * sub-line font: 71px, under the 79px "In use, on site" that the third
 * column's sweep found at parity (a wider line there has failed contrast, and
 * "Not all counted" is 86px).
 */
const PARTIAL_SUB = 'Partial count';

function stat<T>(
  f: Figure<T>,
  base: Omit<PageHeaderStat, 'value' | 'loading'>,
  read: (v: T) => { value: number | null; sub?: string },
): PageHeaderStat {
  if (f.status === 'loading') return { ...base, value: null, loading: true };
  if (f.status === 'failed') return { ...base, value: ABSENT, sub: undefined };
  const r = read(f.value);
  return { ...base, value: displayValue(r.value), ...(r.sub !== undefined ? { sub: r.sub } : {}) };
}

const count = (v: CountFigure) => ({ value: v.total, ...(v.partial ? { sub: PARTIAL_SUB } : {}) });

/**
 * The strip's words. Casing-only keeps the words it has always had; any other
 * mix reads vertical-neutral words no longer than casing's, so the measured
 * clearance holds.
 */
const WORDS = {
  casing: {
    open: { label: 'Open jobs', sub: 'Not completed' },
    approve: { label: 'To approve', sub: 'Field-signed tickets' },
    out: { label: 'Equip out', sub: 'In use, on site' },
  },
  any: {
    open: { label: 'Open work', sub: 'Not completed' },
    approve: { label: 'To approve', sub: 'Awaiting the office' },
    out: { label: 'Equip out', sub: 'In use, on site' },
  },
} as const;

function StripBand({ eyebrow, casingOnly, actions }: { eyebrow: string; casingOnly: boolean; actions?: ReactNode }) {
  const f = useBandFigures();
  const w = casingOnly ? WORDS.casing : WORDS.any;
  const stats: PageHeaderStat[] = [
    stat(f.open, { icon: <Briefcase className="size-3.5" />, ...w.open }, count),
    stat(f.toApprove, { icon: <ClipboardCheck className="size-3.5" />, ...w.approve, tone: 'accent' }, count),
    // The third column is where the hero circle crosses the strip (CLAUDE.md:
    // a ~70px label ceiling). Swept 1024-2560 against Quotes: "In use or on
    // site" (90px) reached the circle's outer ring over a weave line and read
    // up to 0.2 under Quotes' floor at eight widths; this 79px line is at
    // parity everywhere.
    stat(f.equipOut, { icon: <Wrench className="size-3.5" />, ...w.out, tone: 'info' }, count),
    // EXPIRED leads: that person cannot be sent out today. Expiring is a
    // renewal to book and stays on its own line (fe#757, the Safety band).
    stat(f.certs, { icon: <AlertTriangle className="size-3.5" />, label: 'Certs expired' }, (v) => ({
      value: v.expired,
      sub: `${displayValue(v.expiring)} expiring`,
    })),
  ];
  return <PageHeaderBand eyebrow={eyebrow} title="Overview" stats={stats} actions={actions} />;
}

/** Where "+ New Rental Order" lands: Rental Orders, with the new-order wizard open. */
export const NEW_RENTAL_ORDER_PATH = '/rentals/active?new=1';

/** Where "+ New Load" lands: the booking page (a page, so no wizard flag is needed). */
export const NEW_LOAD_PATH = '/trucking/new-load';

export function OverviewBand() {
  const { hasVertical, company } = useAuth();
  const navigate = useNavigate();
  const eyebrow = company?.name || 'Overview';
  const strip = BAND_VERTICALS.some((v) => hasVertical(v));
  // A casing company's band carries no actions, as it never has.
  if (hasVertical('casing_running')) {
    return <StripBand eyebrow={eyebrow} casingOnly={!hasGlobalMoneyVertical(hasVertical)} />;
  }
  // Making a rental, or booking a load, is one click from the Overview
  // (operator, 2026-10-15 / fix/trucking-nav). On the non-casing band only: the
  // casing band is unchanged for every tenant. Each on its own positive guard;
  // a tenant with both gets both, the rental order first as it was.
  const newRental = hasVertical('surface_rentals') ? (
    <button
      type="button"
      data-testid="overview-new-rental-order"
      onClick={() => navigate(NEW_RENTAL_ORDER_PATH)}
      className="fi-action fi-action--standard fi-action--primary"
    >
      <Plus className="size-4" aria-hidden="true" />
      New Rental Order
    </button>
  ) : null;
  const newLoad = hasVertical('trucking') ? (
    <button
      type="button"
      data-testid="overview-new-load"
      onClick={() => navigate(NEW_LOAD_PATH)}
      className="fi-action fi-action--standard fi-action--primary"
    >
      <Plus className="size-4" aria-hidden="true" />
      New Load
    </button>
  ) : null;
  const actions =
    newRental && newLoad ? (
      <div className="flex flex-wrap gap-2">
        {newRental}
        {newLoad}
      </div>
    ) : (newRental ?? newLoad ?? undefined);
  if (strip) return <StripBand eyebrow={eyebrow} casingOnly={false} actions={actions} />;
  return <PageHeaderBand eyebrow={eyebrow} title="Overview" actions={actions} />;
}
